<template>
  <div
    :class="['language-text', {'edit': selected && active}, {'popup-open': popupVisibility}]"
    @click.stop="toggleLanguages"
  >
    <div class="name" v-html="highlightText(getName(names, currentLanguage))" />
    <div v-if="selected" class="language-count">
      <i class="sherpa-icon icon-translate"></i>
      <span :class="['fill', { missing: getFilled < getTotal }]">{{ getFilled }}</span>/<span class="total">{{ getTotal }}</span>
    </div>

    <!-- Popup -->
    <transition name="fade" mode="out-in">
      <builder-popup
        v-show="selected && formBuilderContainer && popupVisibility"
        :popupContainer="formBuilderContainer"
        :isVisible="popupVisibility"
        ref="languages"
        @popupVisibility="changePopupVisibility"
      >
        <div class="popup-container translation">
          <div class="title">
            <i class="sherpa-icon icon-translate"></i>
            <span>{{ getLang('translations') }} <strong><em>{{ id }}</em></strong></span>
          </div>
          <div class="popup-content translation-languages">
            <div v-for="(name, language) in names" :key="language"
              :class="['translation-language', {'selected': language === selectedLanguage}]"
              @click="changeSelectedLanguage(language)"
            >
              <div class="language-key">{{ language }}</div>
              <div class="name">
                <template v-if="language === selectedLanguage">
                  <QuillEditor v-if="language === selectedLanguage"
                    v-model:content="names[language]"
                    content-type="html"
                    :toolbar="simpleHtmlCustomToolbar"
                    :options="editorOptions"
                    :readOnly="false"
                    @update:content="fixHtml(language)"
                  />
                </template>
                <template v-else>
                  <span class="not-selected" v-html="getName(names, language)" />
                </template>
                <template v-if="names[language] !== ''">
                  <transition name="fade-slide-left" mode="out-in">
                    <div :class="['buttons-container', {'selected': language === selectedLanguage}]">
                      <transition  name="fade-slide-left" mode="out-in">
                        <i v-if="!translating && getFilled < getTotal" :class="['sherpa-icon', 'icon-translate']" @click.stop="translate(language)"></i>
                        <spinner-loading v-else-if="getFilled < getTotal" :class="['spinner-loading']" :line-size='2' line-fg-color='#d9614b' :size="18"/>
                      </transition>
                      <i :class="['sherpa-icon', 'icon-borrar']" @click.stop="deleteText(language)"></i>
                    </div>
                  </transition>
                </template>
              </div>
            </div>
          </div>
        </div>
      </builder-popup>
    </transition>
  </div>
</template>

<script>
import builderPopup from '../builderPopup'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import Configuration from '@/configuration'
import translateHTML from '@/components/content/definitionLoader/utils/translateHTML'
import simplebar from 'simplebar-vue'

export default {
  name: 'language-text',
  components: {
    builderPopup,
    QuillEditor,
    simplebar
  },
  props: {
    id: String,
    names: Object,
    getName: Function,
    getLang: Function,
    selected: Boolean,
    currentLanguage: String,
    active: Boolean,
    formBuilderContainer: HTMLElement,
    highlightTerm: { type: String, default: '' }
  },
  data () {
    return {
      popupVisibility: false,
      simpleHtmlCustomToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ header: 1 }, { header: 2 }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['link'],
        [{ direction: 'rtl' }],
        ['clean']
      ],
      editorOptions: {
        clipboard: {
          matchVisual: false // Evita que se aplique un bloque por defecto
        }
      },
      selectedLanguage: null,
      translateKey: Configuration.value('API_OPENAI_TRANSLATE_KEY'),
      translateUrl: Configuration.value('API_OPENAI_TRANSLATE_URL'),
      translating: false
    }
  },
  mounted () {
    this.selectedLanguage = this.currentLanguage
  },
  beforeUnmount () {
  },
  methods: {
    highlightText (text) {
      if (!this.highlightTerm || !text) return text
      const norm = s => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      const normText = norm(text)
      const normTerm = norm(this.highlightTerm)
      if (!normTerm) return text
      let result = '', i = 0
      while (i < text.length) {
        if (normText.substring(i, i + normTerm.length) === normTerm) {
          result += '<mark>' + text.substring(i, i + normTerm.length) + '</mark>'
          i += normTerm.length
        } else { result += text[i]; i++ }
      }
      return result
    },
    toggleLanguages (event) {
      // Asegúrate de que el popup se esté actualizando correctamente
      if (event.target.classList.contains('overlay')) {
        this.popupVisibility = false
      } else if (this.active) {
        this.popupVisibility = true
        this.selectedLanguage = this.currentLanguage
      }
    },
    changePopupVisibility (visibility) {
      this.popupVisibility = visibility
      this.$emit('languageTextPopupOpen', this.popupVisibility)
    },
    fixHtml (language) {
      this.names[language] = this.names[language]
        .replace(/<p[^>]*>/g, '')
        .replace(/<\/p>/g, '')
    },
    changeSelectedLanguage (language) {
      this.selectedLanguage = language
    },
    deleteText (language) {
      this.names[language] = ''
    },
    translate (originLanguage) {
      this.translating = true
      this.$nextTick(() => {
        setTimeout(() => {
          Object.keys(this.names).forEach((key) => {
            const value = this.names[key]
            if (!value || value === '') {
              translateHTML(
                this.translateUrl,
                this.translateKey,
                this.names[originLanguage],
                originLanguage,
                key
              )
                .then(translation => {
                  this.names[key] = translation
                  this.translating = false
                })
                .catch(err => {
                  console.error(err)
                  this.translating = false
                })
            }
          })
        }, 500)
      })
    }
  },
  computed: {
    getFilled () {
      let total = 0
      Object.keys(this.names).forEach((key) => {
        const value = this.names[key]
        if (value && value !== '') {
          total++
        }
      })
      return total
    },
    getTotal () {
      return Object.keys(this.names).length
    }
  },
  watch: {
    names: {
      handler (newVal, oldVal) {
        this.$emit('textChange', newVal)
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
mark {
  background-color: #fff176;
  padding: 0;
}

.language-text {
  display: inline-block;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 2px;
  padding: 1px 6px 1.5px 3px;
  position: relative;
  margin: 1px 0 0 1px;

  .name {
    p,
    pre {
      margin: 0;
    }
  }

  .language-count {
    display: inline-block;
    margin-left: 10px;
    color: #4ab800c5;
    font-size: 11px;
    font-family: Verdana, Geneva, Tahoma, sans-serif;
    font-weight: 100;
    transition: color 0.2s ease;
    position: absolute;
    top: 3px;
    right: 5px;

    i {
      font-size: 14px;
      margin-right: 0;
      color: #4ab800c5;
      vertical-align: middle;
      transition: color 0.2s ease;
    }

    .missing {
      color: #e30000;
      transition: color 0.2s ease;
    }
  }

  &.edit {
    transition: all 0.2s ease;
    cursor: pointer;

    &.popup-open,
    &:hover {
      color: #076cfabe;
      background-color: #e4fffe;
      border: 1px dotted #999;

      .language-count {
        color: #076cfabe !important;

        i {
          color: #076cfabe !important;
        }

        .missing {
          color: #076cfabe !important;
        }
      }
    }
  }
}
</style>
